import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { useAdvice } from "../../hooks/useDeploy";
import { errorMessage } from "../../hooks/useServers";

// «¿Qué hago?» junto a un problema del despliegue: explica qué pasó en palabras sencillas y ofrece botones. Los botones son SOLO pasos que la ventana ya sabe hacer
// (los manda `handlers`, cada uno con sus propias confirmaciones); la sugerencia nunca ejecuta nada por sí sola. `resetKey` cambia cuando aparece un problema nuevo.
export function DeployAdvice({ projectId, kind, name, options, resetKey, handlers }) {
  const advice = useAdvice(projectId);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState("");
  const id = `deploy-advice-${name}`;

  useEffect(() => {
    setData(null);
    setError("");
    setCopied(false);
    setManual("");
  }, [resetKey]);

  const ask = async () => {
    setError("");
    setCopied(false);
    setManual("");
    try {
      setData(await advice.mutateAsync({ kind, ...options }));
    } catch (e) {
      setData(null);
      setError(errorMessage(e, "No pude preparar la sugerencia. Inténtalo de nuevo."));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setManual(text); // sin permiso para el portapapeles: se muestra el texto para copiarlo a mano
    }
  };

  const run = (action) => (action.id === "ask_chat" ? copy(data.chat_text) : handlers?.[action.id]?.());
  const actions = (data?.actions || []).filter((a) => a.id === "ask_chat" || handlers?.[a.id]);
  const cost = data?.source === "ia" ? data.cost_usd : null;

  return (
    <div data-testid={id} data-kind={kind} className="space-y-2">
      {!data && (
        <Button data-testid={`${id}-ask`} size="sm" variant="outline" onClick={ask} disabled={advice.isPending}>
          {advice.isPending ? "Pensando…" : "💡 ¿Qué hago?"}
        </Button>
      )}
      {error && <p data-testid={`${id}-error`} className="text-xs text-amber-500">{error}</p>}
      {data && (
        <div data-testid={`${id}-panel`} className="rounded-lg border border-sky-500/40 p-3 space-y-2">
          <p className="text-xs text-muted-foreground" data-testid={`${id}-source`}>{data.source === "ia" ? "💡 Sugerencia de la IA" : "💡 Sugerencia automática"}</p>
          <p data-testid={`${id}-text`} className="text-sm">{data.explanation}</p>
          {data.note && <p data-testid={`${id}-note`} className="text-xs text-amber-500">{data.note}</p>}
          <div className="flex flex-wrap gap-2">
            {actions.map((a) => (
              <Button key={a.id} data-testid={`${id}-action-${a.id}`} size="sm" variant="outline" onClick={() => run(a)}>{a.label}</Button>
            ))}
          </div>
          {copied && <p data-testid={`${id}-copied`} className="text-xs text-emerald-500">✅ Copiado. Pégalo en el chat del proyecto.</p>}
          {manual && <p data-testid={`${id}-manual`} className="text-xs break-words select-all">No pude copiarlo solo. Selecciónalo y cópialo: {manual}</p>}
          {cost !== null && cost !== undefined && (
            <p data-testid={`${id}-cost`} className="text-xs text-muted-foreground">
              {data.cached ? "Ya tenía esta sugerencia guardada: sin costo." : cost < 0.01 ? "Costo de esta sugerencia: menos de 1 centavo de dólar." : `Costo de esta sugerencia: ≈ $${cost.toFixed(2)} USD.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
